<script setup lang="ts">
/**
 * Home — 主页（区块锚点滚动）
 *
 * 区块顺序（2026-08-29 改版：新增效果对比区，合并安装/使用 teaser 为快速上手）：
 * 1. Hero（品牌 + CTA + 产品主视觉 ConsoleShowcase + popup 浮动卡）
 * 2. WhatIsYfb（什么是隐分宝：3 痛点 + 一句插件介绍）
 * 3. Preview（启用前/后效果对比，BeforeAfter）
 * 4. UseCases（谁需要隐分宝：3 用户场景）
 * 5. Site Grid（支持站点，9 卡片）
 * 6. Features（8 卡 Z 字形，#2 信息隐藏是 HERO 含 InfoHideMockup）
 * 7. Terminal Support（浏览器 × 平台矩阵）
 * 8. QuickStart（快速上手：电脑/手机 tab × 3 步 + 友情提示 + 完整指南 CTA）
 * 9. FAQ（常见问题）
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSeo } from '@/composables/useSeo'
import HeroBlock from '@/components/HeroBlock.vue'
import WhatIsYfb from '@/components/WhatIsYfb.vue'
import SiteGrid from '@/components/SiteGrid.vue'
import FeatureRow from '@/components/FeatureRow.vue'
import QuickStart from '@/components/QuickStart.vue'
import BeforeAfter from '@/components/BeforeAfter.vue'
import UseCases from '@/components/UseCases.vue'
import BrowserMatrix from '@/components/BrowserMatrix.vue'
import InfoHideMockup from '@/components/InfoHideMockup.vue'
import MatchNavigationMockup from '@/components/MatchNavigationMockup.vue'
import ProgressiveMockup from '@/components/ProgressiveMockup.vue'
import OverlayMockup from '@/components/OverlayMockup.vue'
import CoverIntroMockup from '@/components/CoverIntroMockup.vue'
import SkipButtonsMockup from '@/components/SkipButtonsMockup.vue'
import ProgressProtectionMockup from '@/components/ProgressProtectionMockup.vue'
import FaqAccordion from '@/components/FaqAccordion.vue'
import { useSupportedSites } from '@/composables/useSupportedSites'

const { t, tm } = useI18n()

// 支持站点：名称随界面语言（三语），数据与 About 页同源
const supportedSites = useSupportedSites()

// SEO: Home + JSON-LD SoftwareApplication
// name / alternateName / description 全部从 i18n 取（见 product.* 命名空间，docs/mainadr/0008）。
useSeo({
  page: 'home',
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '隐分宝',
      alternateName: 'Replay Shield',
      url: 'https://woofootball.site',
      logo: 'https://woofootball.site/icons/favicon.png'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '隐分宝',
      alternateName: 'Replay Shield',
      url: 'https://woofootball.site',
      inLanguage: ['zh-CN', 'zh-TW', 'en']
    },
    {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: t('app.name'),
    alternateName: '隐分宝',
    applicationCategory: 'BrowserApplication',
    applicationSubCategory: 'Browser Extension',
    operatingSystem: 'Windows, macOS, Linux, Android',
    description: t('product.shortIntro'),
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'CNY'
    },
    url: 'https://woofootball.site',
    softwareRequirements: 'Chrome 88+, Edge 88+, Firefox 78+',
    inLanguage: ['zh-CN', 'zh-TW', 'en']
    }
  ]
})

const featureCards = computed(() => {
  const ref = tm('home.features.cards') as any
  const cards = ref?.value ?? ref
  return Array.isArray(cards) ? cards : []
})

const matrixData = computed(() => {
  const ref = tm('home.matrix') as any
  const m = ref?.value ?? ref
  return {
    headers: m?.headers || [],
    platforms: m?.platforms || [],
    rows: m?.rows || []
  }
})

const faqItems = computed(() => {
  const ref = tm('home.faq.items') as any
  const items = ref?.value ?? ref
  return Array.isArray(items) ? items : []
})
</script>

<template>
  <div class="home">
    <!-- 1. Hero -->
    <HeroBlock />

    <!-- 2. 什么是隐分宝 -->
    <WhatIsYfb />

    <!-- 3. 启用前 / 启用后 效果对比 -->
    <section id="preview" class="section">
      <div class="section__inner">
        <h2 class="section__title">
          <span class="section__title-bg">{{ t('home.sections.preview') }}</span>
        </h2>
        <p class="section__subtitle">{{ t('home.preview.subtitle') }}</p>

        <BeforeAfter />
      </div>
    </section>

    <!-- 4. 谁需要隐分宝 -->
    <section id="use-cases" class="section">
      <div class="section__inner">
        <h2 class="section__title">
          <span class="section__title-bg">{{ t('home.sections.useCases') }}</span>
        </h2>
        <p class="section__subtitle">{{ t('home.useCases.subtitle') }}</p>

        <UseCases />
      </div>
    </section>

    <!-- 5. Site Grid -->
    <section id="sites-grid" class="section">
      <div class="section__inner">
        <h2 class="section__title">
          <span class="section__title-bg">{{ t('home.sections.sites') }}</span>
        </h2>
        <p class="section__subtitle">{{ t('home.sections.sitesSubtitle') }}</p>
      </div>
      <SiteGrid :items="supportedSites" />
    </section>

    <!-- 6. Features（8 卡 Z 字形） -->
    <section id="features" class="section">
      <div class="section__inner">
        <h2 class="section__title">
          <span class="section__title-bg">{{ t('home.sections.features') }}</span>
        </h2>
        <p class="section__subtitle">{{ t('home.features.subtitle') }}</p>

        <div class="features__list">
          <FeatureRow
            v-for="(card, idx) in featureCards"
            :key="card.id || card.title"
            :title="card.title"
            :subtitle="card.subtitle"
            :desc="card.desc"
            :icon="card.icon"
            :side="idx % 2 === 0 ? 'right' : 'left'"
            :highlight="card.id === 'info-hide'"
          >
            <InfoHideMockup v-if="card.id === 'info-hide'" />
            <MatchNavigationMockup v-else-if="card.id === 'match-list'" />
            <ProgressiveMockup v-else-if="card.id === 'progressive'" />
            <OverlayMockup v-else-if="card.id === 'mask'" />
            <CoverIntroMockup v-else-if="card.id === 'cover-intro'" />
            <SkipButtonsMockup v-else-if="card.id === 'skip-buttons'" />
            <ProgressProtectionMockup v-else-if="card.id === 'progress-protection'" />
          </FeatureRow>
        </div>
      </div>
    </section>

    <!-- 6.5 订阅 CTA 条（免费 vs Pro，衔接功能特性 → 引导订阅页） -->
    <section class="section">
      <div class="section__inner">
        <div class="home__cta-subscribe">
          <div class="home__cta-subscribe-text">
            <h3 class="home__cta-subscribe-title">{{ t('home.features.ctaSubscribe.title') }}</h3>
            <p class="home__cta-subscribe-desc">{{ t('home.features.ctaSubscribe.desc') }}</p>
          </div>
          <a href="/subscribe" class="home__cta-subscribe-btn yfb-press">
            {{ t('home.features.ctaSubscribe.cta') }} →
          </a>
        </div>
      </div>
    </section>

    <!-- 7. Terminal Support（终端支持矩阵） -->
    <section id="matrix" class="section">
      <div class="section__inner">
        <h2 class="section__title">
          <span class="section__title-bg">{{ t('home.sections.matrix') }}</span>
        </h2>
        <p class="section__subtitle">{{ t('home.matrix.subtitle') }}</p>

        <BrowserMatrix
          :headers="matrixData.headers"
          :platforms="matrixData.platforms"
          :rows="matrixData.rows"
          :header-icons="['chrome', 'edge', 'firefox']"
          :platform-icons="['ph:desktop-bold', 'ph:device-mobile-bold', 'ph:device-mobile-bold', 'ph:device-mobile-bold']"
        />
      </div>
    </section>

    <!-- 8. 快速上手（安装 + 首次使用，合并原 #install / #workflow） -->
    <section id="quickstart" class="section">
      <div class="section__inner">
        <h2 class="section__title">
          <span class="section__title-bg">{{ t('home.sections.quickStart') }}</span>
        </h2>
        <p class="section__subtitle">{{ t('home.quickStart.subtitle') }}</p>

        <QuickStart />
      </div>
    </section>

    <!-- 9. FAQ -->
    <section id="faq" class="section">
      <div class="section__inner">
        <h2 class="section__title">
          <span class="section__title-bg">{{ t('home.sections.faq') }}</span>
        </h2>
        <p class="section__subtitle">{{ t('home.faq.subtitle') }}</p>

        <FaqAccordion :items="faqItems" />
      </div>
    </section>
  </div>
</template>

<style scoped>
.home {
  width: 100%;
}

.section {
  padding: var(--yfb-sp-9) var(--yfb-sp-6);
  border-bottom: 1px solid var(--yfb-rule-soft);
  scroll-margin-top: var(--yfb-header-h);
}

.section__inner {
  max-width: 1200px;
  margin: 0 auto;
}

.section__title {
  margin: 0 0 var(--yfb-sp-4);
  font-family: var(--yfb-font-heading);
  font-size: clamp(var(--yfb-fs-2xl), 4vw, var(--yfb-fs-4xl));
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-display);
  line-height: 1.1;
  text-wrap: balance;
}

.section__title-bg {
  display: inline-block;
  padding: var(--yfb-sp-2) var(--yfb-sp-4);
  background-color: var(--yfb-surface);
  color: var(--yfb-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  box-shadow: 0 0 12px var(--yfb-glow-soft);
  text-shadow: 0 0 10px var(--yfb-glow);
}

.section__subtitle {
  font-size: var(--yfb-fs-lg);
  color: var(--yfb-text-secondary);
  margin: 0 0 var(--yfb-sp-7);
  max-width: 65ch;
  line-height: var(--yfb-lh-lead);
}

.features__list {
  display: flex;
  flex-direction: column;
  gap: var(--yfb-sp-6);
}

/* 订阅 CTA 条（免费 vs Pro） */
.home__cta-subscribe {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--yfb-sp-6);
  padding: var(--yfb-sp-7) var(--yfb-sp-8);
  background-image: var(--yfb-grad-cta);
  color: var(--yfb-on-accent);
  border: 1px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: 0 0 18px var(--yfb-glow), 0 8px 32px var(--yfb-drop);
}

.home__cta-subscribe-text {
  flex: 1;
}

.home__cta-subscribe-title {
  font-family: var(--yfb-font-heading);
  font-size: var(--yfb-fs-2xl);
  font-weight: 700;
  margin: 0 0 8px;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-button);
}

.home__cta-subscribe-desc {
  font-size: var(--yfb-fs-md);
  margin: 0;
  opacity: 0.85;
  line-height: var(--yfb-lh-body);
  max-width: 60ch;
}

.home__cta-subscribe-btn {
  flex-shrink: 0;
  padding: 12px 28px;
  border: 1px solid var(--yfb-on-accent);
  border-radius: var(--yfb-radius-md);
  color: var(--yfb-on-accent);
  background: rgba(11, 11, 16, 0.25);
  font-weight: 700;
  font-size: var(--yfb-fs-base);
  text-decoration: none;
  transition: filter var(--yfb-anim-fast) ease-out, box-shadow var(--yfb-anim-fast) ease-out, transform var(--yfb-anim-fast) ease-out;
}

.home__cta-subscribe-btn:hover {
  filter: brightness(1.12);
  box-shadow: 0 0 16px var(--yfb-glow);
  transform: translateY(-1px);
}

.home__cta-subscribe-btn:focus-visible {
  outline: 2px solid var(--yfb-focus);
  outline-offset: 4px;
}

@media (max-width: 767px) {
  .home__cta-subscribe {
    flex-direction: column;
    align-items: flex-start;
    padding: var(--yfb-sp-6);
  }
  .home__cta-subscribe-title {
    font-size: var(--yfb-fs-lg);
  }
  .home__cta-subscribe-btn {
    width: 100%;
    text-align: center;
  }
  .section {
    padding: var(--yfb-sp-8) var(--yfb-sp-4);
  }
  .section__title {
    font-size: var(--yfb-fs-2xl);
  }
}
</style>
